'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import {
  Container, Paper, Title, Text, TextInput, Button, Stack, Alert, Divider, Group,
} from '@mantine/core';
import { IconMail, IconFingerprint, IconCheck, IconAlertCircle } from '@tabler/icons-react';
import { startAuthentication } from '@simplewebauthn/browser';
import { requestMagicLink, passkeyLoginOptions, passkeyLogin } from '@/lib/account';
import { useUser } from '@/context/UserContext';
import { trackEvent } from '@/lib/telemetry';

export default function AccountLoginPage() {
  const router = useRouter();
  const { account, completeLogin } = useUser();
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (account) router.replace('/account');
  }, [account, router]);

  if (account) return null;

  const handleMagicLink = async (e) => {
    e.preventDefault();
    trackEvent('Account Login Method Selected', { method: 'magic_link' });
    setError('');
    setSending(true);
    try {
      await requestMagicLink(email);
      setSent(true);
      trackEvent('Account Login Succeeded', { method: 'magic_link', stage: 'link_sent' });
    } catch (err) {
      setError(err.message);
      trackEvent('Account Login Failed', { method: 'magic_link', reason: 'request_error' });
    } finally {
      setSending(false);
    }
  };

  const handlePasskey = async () => {
    trackEvent('Account Login Method Selected', { method: 'passkey' });
    setError('');
    setPasskeyBusy(true);
    try {
      const { options, key } = await passkeyLoginOptions();
      const response = await startAuthentication({ optionsJSON: options });
      const result = await passkeyLogin(key, response);
      await completeLogin(result);
      trackEvent('Account Login Succeeded', { method: 'passkey', stage: 'authenticated' });
      router.push('/account');
    } catch (err) {
      // Gebruiker annuleerde de passkey-prompt → geen foutmelding nodig
      if (err.name !== 'NotAllowedError') {
        setError(err.message || 'Passkey login failed');
      }
      trackEvent('Account Login Failed', {
        method: 'passkey',
        reason: err.name === 'NotAllowedError' ? 'cancelled' : 'authentication_error',
      });
    } finally {
      setPasskeyBusy(false);
    }
  };

  return (
    <Container size="xs" py={60}>
      <Paper shadow="md" p={30} radius="lg" withBorder>
        <Stack gap="md">
          <div>
            <Title order={2}>Log in</Title>
            <Text c="dimmed" size="sm" mt={4}>
              No password needed. Use a passkey or get a login link by email.
              New here? Logging in automatically creates your account.
            </Text>
          </div>

          {error && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" radius="md">
              {error}
            </Alert>
          )}

          <Button
            leftSection={<IconFingerprint size={20} />}
            size="md"
            onClick={handlePasskey}
            loading={passkeyBusy}
          >
            Log in with passkey
          </Button>

          <Divider label="or" labelPosition="center" />

          {sent ? (
            <Alert icon={<IconCheck size={16} />} color="green" radius="md">
              Check your inbox! We sent a login link to <strong>{email}</strong>.
              The link is valid for 15 minutes.
            </Alert>
          ) : (
            <form onSubmit={handleMagicLink}>
              <Stack gap="sm">
                <TextInput
                  label="Email address"
                  placeholder="you@example.com"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  leftSection={<IconMail size={16} />}
                  required
                />
                <Button type="submit" variant="light" loading={sending}>
                  Send me a login link
                </Button>
              </Stack>
            </form>
          )}

          <Group justify="center">
            <Text size="xs" c="dimmed" ta="center">
              With an account your check progress, favorites and matching
              contributions sync across devices.
            </Text>
          </Group>
        </Stack>
      </Paper>
    </Container>
  );
}
